import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import HtmlIcon from '@mui/icons-material/Html';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, TextField, Typography } from '@mui/material';
import React from 'react';
import { HtmlOutput } from '../HtmlOutput';
import { HtmlOutputHelp } from '../HtmlOutput/HtmlOutputHelp';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { type } from './types';
const WidgetFormHtml = (props) => {
    var _a, _b, _c, _d, _e;
    const value = props.getDataString();
    const error = getVisibleError(props);
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, variant: 'outlined', size: 'medium', error: !!error, disabled: props.config.disabled },
        React.createElement(Grid, { container: true, spacing: 2 },
            React.createElement(Grid, { item: true, xs: 6 },
                React.createElement(TextField, { label: props.config.label, variant: 'outlined', fullWidth: true, size: 'medium', required: (_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required, error: !!error, value: value, multiline: true, rows: props.config.rows, disabled: props.config.disabled, inputProps: {
                        style: { fontFamily: 'monospace' },
                        tabIndex: props.editMode ? -1 : undefined,
                        'aria-describedby': ariaDescribedBy,
                    }, onChange: (event) => {
                        const newValue = event.target.value;
                        props.setData(newValue);
                        renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
                    }, autoFocus: (_b = (!props.editMode && props.config.autoFocus)) !== null && _b !== void 0 ? _b : false })),
            React.createElement(Grid, { item: true, xs: 6 },
                React.createElement(Button, { variant: 'outlined', onClick: () => {
                        props.internalData.showExample = true;
                        props.update();
                        renderForm(props);
                    } }, "Show Examples"),
                React.createElement(Typography, { component: 'div', sx: { pt: 2 } },
                    React.createElement(HtmlOutput, { html: value, customTags: (_c = props.rootParams.globalWidgetConfig) === null || _c === void 0 ? void 0 : _c.htmlOutputCustomTags, data: (_d = props.rootParams.globalWidgetConfig) === null || _d === void 0 ? void 0 : _d.htmlOutputData, customIcons: (_e = props.rootParams.globalWidgetConfig) === null || _e === void 0 ? void 0 : _e.htmlOutputIcons })))),
        React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error }),
        React.createElement(Dialog, { open: !!props.internalData.showExample, maxWidth: 'xl', fullWidth: true, onClose: (event, reason) => {
                if (reason === 'escapeKeyDown') {
                    delete props.internalData.showExample;
                    props.update();
                    renderForm(props);
                }
            } },
            React.createElement(DialogTitle, null, "HTML Examples"),
            React.createElement(DialogContent, null,
                ' ',
                React.createElement(HtmlOutputHelp, null)),
            React.createElement(DialogActions, null,
                React.createElement(Button, { variant: 'outlined', onClick: () => {
                        delete props.internalData.showExample;
                        props.update();
                        renderForm(props);
                    } }, "Close")))));
};
const WidgetFormHtmlEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: 'HTML Template: ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            label: true,
            disabled: true,
            autoFocus: true,
        }), buildValidateTab(props, {
            required: true,
        }), buildHiddenTab(props), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormHtml, Object.assign({}, props))));
};
export const widgetFormHtml = {
    type,
    validate: (props) => {
        var _a, _b;
        if (isSkipValidate(props)) {
            return;
        }
        const value = props.getDataStringOrUndefined();
        if ((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) {
            if (!value) {
                return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : (props.config.label ? props.config.label + ' is required' : 'Required');
            }
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), props.getDataStringOrUndefined());
    },
    component: WidgetFormHtml,
    icon: React.createElement(HtmlIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormHtmlEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'html', label: 'HTML' }),
};
